/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material';

import { useConfirmationDialog } from '../../../contexts/useConfirmationDialog';
import { usePage } from '../../../contexts/usePageContext';
import { getConfigStorageItem } from '../../../hooks/useConfigStorage';
import {
 changeCurrentPreset,
 ColPresetType,
 createClientConfigurationStart,
 getClientConfigurationsStart,
 updateClientConfigurationStart,
} from '../../../redux/clientConfiguration/clientConfigurationSlice';
import { isPresetChanged } from '../../../utils/comparePresets';
import { RootState } from '../../../redux/store';
import { updateConfData } from '../ColModal/utils';

export const askName = (configurationItem: any) => {
 const enteredName = prompt('name:');

 if (!enteredName) {
  alert('Empty names not allowed');
  return false;
 }

 if ((configurationItem || []).find((item: any) => item.name === enteredName)) {
  alert('Name already exists');
  return false;
 }
 return enteredName || false;
};

const useModalConf = () => {
 const confName = 'columns';
 const dispatch = useDispatch();
 const { t } = useTranslation();
 const th = useTheme();

 const { pageName } = usePage();

 const { openDialog: openConfirmationDialog } = useConfirmationDialog();

 const clientConfigurationSelector = useSelector(
  (state: RootState) => state.clientConfiguration,
 );

 const currentColumnsPreset =
  clientConfigurationSelector.currentColumnsPreset || null;
 const tempColumnsPreset =
  clientConfigurationSelector.tempColumnsPreset || null;

 const { success, clientConfigurations, presetsColumns } =
  clientConfigurationSelector;

 const configurationItem = clientConfigurations?.find(
  (item) => item.Parameter === confName,
 );
 const configurationItemValue = JSON.parse(
  configurationItem?.Value || '{}',
 ) as { [key: string]: ColPresetType[] };

 const currentPresets = presetsColumns?.[pageName];
 const currentPreset: ColPresetType | null = (tempColumnsPreset?.[pageName] ||
  currentColumnsPreset?.[pageName]) as ColPresetType;

 const [saveMenuAnchorEl, setSaveMenuAnchorEl] = useState<null | HTMLElement>(
  null,
 );

 const wasChanged =
  currentPreset &&
  currentPreset?.name &&
  isPresetChanged(currentPresets, currentPreset);

 const [lastCreated, setLastCreated] = useState<string | null>(null);

 const handleCreate = () => {
  const askedName = askName(configurationItemValue?.[pageName] || []);
  if (!askedName) return;

  const name = askedName || 'default';

  const newEntry = {
   ...(currentPreset || {}),
   name,
  };

  const val = updateConfData(
   configurationItem?.Id || '-1',
   {
    ...configurationItemValue,
    [pageName]: [...(configurationItemValue?.[pageName] || []), newEntry],
   },
   confName,
  );

  if (!configurationItem?.Id) {
   dispatch(createClientConfigurationStart(val));
  } else {
   dispatch(updateClientConfigurationStart(val));
  }

  setLastCreated(name);
 };

 const handleSave = () => {
  if (configurationItem === undefined) return;
  const val = updateConfData(
   configurationItem?.Id || '-1',
   {
    ...configurationItemValue,
    [pageName]: configurationItemValue?.[pageName]?.map((item) => {
     if (item.name === currentPreset?.name) {
      return currentPreset;
     }
     return item;
    }),
   },
   confName,
  );
  if (!configurationItem?.Id) {
   dispatch(createClientConfigurationStart(val));
  } else {
   dispatch(updateClientConfigurationStart(val));
  }
 };

 const handleDelete = () => {
  if (!configurationItem) return;

  openConfirmationDialog({
   title: t('global.delete'),
   message: t('global.deleteMessage'),
   onConfirm: () => {
    const newEntry = configurationItemValue?.[pageName]?.filter(
     (item: any) => item.name !== currentPreset?.name,
    );
    const val = updateConfData(
     configurationItem?.Id || '-1',
     {
      ...configurationItemValue,
      [pageName]: newEntry,
     },
     confName,
    );
    if (!configurationItem?.Id) {
     dispatch(createClientConfigurationStart(val));
    } else {
     dispatch(updateClientConfigurationStart(val));
    }
   },
  });
 };

 const handleDuplicate = () => {
  if (!configurationItem) return;

  const askedName = askName(currentPresets);
  if (!askedName) return;

  const name = askedName || 'default';

  const newItem = configurationItemValue?.[pageName]?.find(
   (i) => i.name === currentPreset?.name,
  );
  const newEntry = [
   ...(configurationItemValue?.[pageName] || []),
   { ...newItem, name },
  ];
  const val = updateConfData(
   configurationItem?.Id || '-1',
   {
    ...configurationItemValue,
    [pageName]: newEntry,
   },
   confName,
  );
  if (!configurationItem?.Id) {
   dispatch(createClientConfigurationStart(val));
  } else {
   dispatch(updateClientConfigurationStart(val));
  }
 };

 const handleProChange = () => {
  const herePreset =
   tempColumnsPreset?.[pageName] || currentColumnsPreset?.[pageName];
  if (!herePreset) return;

  dispatch(
   changeCurrentPreset({
    page: pageName,
    type: confName,
    preset: {
     ...(herePreset as ColPresetType),
     isPro: !(herePreset || {})?.isPro,
    },
    temp: true,
   }),
  );
 };

 const handleSelectPreset = (preset: ColPresetType) => {
  dispatch(
   changeCurrentPreset({
    page: pageName,
    type: confName,
    preset,
    temp: true,
   }),
  );
 };

 const handleRevert = () => {
  if (!currentPreset) return;
  const revertedPreset = currentPresets?.find(
   (item) => item.name === currentPreset?.name,
  );
  if (confName === 'columns') {
   dispatch(
    changeCurrentPreset({
     page: pageName,
     type: 'columns',
     preset: revertedPreset as ColPresetType,
     temp: true,
    }),
   );
  }
 };

 const handleCreateAndClose = () => {
  handleCreate();
  setSaveMenuAnchorEl(null);
 };

 useEffect(() => {
  if (currentColumnsPreset?.[pageName]) return;
  const lsPreset = getConfigStorageItem(pageName)?.columns;
  if (lsPreset) {
   dispatch(
    changeCurrentPreset({
     page: pageName,
     type: 'columns',
     preset: lsPreset,
     temp: false,
    }),
   );
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, []);

 useEffect(() => {
  dispatch(
   changeCurrentPreset({
    page: pageName,
    type: 'columns',
    preset: currentColumnsPreset?.[pageName] as ColPresetType,
    temp: true,
   }),
  );
 }, [currentColumnsPreset]);

 useEffect(() => {
  if (
   success?.createClientConfiguration ||
   success?.updateClientConfiguration
  ) {
   dispatch(getClientConfigurationsStart({ cached: false }));
  }
  if (success?.getClientConfigurations && lastCreated) {
   const preset = currentPresets?.find((p) => p.name === lastCreated);
   if (preset) {
    dispatch(
     changeCurrentPreset({
      page: pageName,
      type: confName,
      preset,
      temp: true,
     }),
    );
   }
  }
 }, [success]);

 const addDisabled =
  (currentPreset as ColPresetType)?.columns &&
  typeof (currentPreset as ColPresetType).columns === 'object'
   ? Object.keys((currentPreset as ColPresetType).columns).length === 0
   : Array.isArray((currentPreset as ColPresetType)?.columns) &&
     (currentPreset as ColPresetType).columns.length === 0;

 const isProChecked =
  (tempColumnsPreset?.[pageName] || currentColumnsPreset?.[pageName])?.isPro ||
  false;

 return {
  confName,
  th,
  t,
  currentPresets,
  currentPreset,
  wasChanged,
  addDisabled,
  saveMenuAnchorEl,
  isProChecked,
  handleCreate,
  handleSave,
  handleDelete,
  handleDuplicate,
  handleProChange,
  handleSelectPreset,
  handleRevert,
  handleCreateAndClose,
 };
};

export default useModalConf;
